import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Stack,
  useTheme,
  Grid,
  Paper,
} from '@mui/material';
import Hero from '../components/Hero';
import cgcsLogo from '../assets/img/cgcs_final_logo_RGB_Color vertical.jpg';

// Add your sponsor logos here
const sponsors = [
  {
    name: 'CGCS',
    logo: cgcsLogo,
  },
  {
    name: 'Sponsor US',
    logo: 'https://placehold.co/400x200/4d1979/ffffff?text=Sponsor US',
  },
  {
    name: 'Sponsor US',
    logo: 'https://placehold.co/400x200/4d1979/ffffff?text=Sponsor US',
  },
  {
    name: 'Sponsor US',
    logo: 'https://placehold.co/400x200/4d1979/ffffff?text=Sponsor US',
  },
  // Add more sponsors as needed
];

const benefitsList = [
  {
    title: 'Access to Talent',
    description: 'Gain access to our diverse and skilled member base of Austin Community College students, eager to excel in computing and technology.',
  },
  {
    title: 'Build Your Brand',
    description: "Establish your brand name and cultivate meaningful relationships with ACC's future tech professionals by partnering with the leading computing organization on campus.",
  },
  {
    title: 'Showcase Opportunities',
    description: 'Highlight the exciting opportunities and unique culture at your company to inspire and attract passionate students.',
  },
  {
    title: 'Empower Students',
    description: 'Make a difference by guiding and supporting ACC students, especially those exploring the diverse fields of computing and technology.',
  },
];

const Sponsor = () => {
  const theme = useTheme();

  return (
    <>
      <Hero
        title="Partner with ACM at ACC"
        description="Join us in empowering the next generation of developers, designers, and tech leaders. Your support fuels impactful learning experiences, innovative projects, and community growth."
        primaryButtonText="Become a Partner"
        primaryButtonLink="#contact-box"
      />

      {/* Sponsors Carousel Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Stack spacing={6} alignItems="center">
            <Typography variant="h2" align="center" sx={{ fontSize: { xs: '2rem', md: '2.5rem' }, fontWeight: 700 }}>
              Our Sponsors
            </Typography>

            <Box 
              sx={{ 
                width: '100%', 
                overflow: 'hidden',
                position: 'relative'
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  animation: 'scroll 20s linear infinite',
                  '@keyframes scroll': {
                    '0%': {
                      transform: 'translateX(0)',
                    },
                    '100%': {
                      transform: 'translateX(-50%)',
                    },
                  },
                }}
              >
                {/* Double the sponsors array to create seamless loop */}
                {[...sponsors, ...sponsors].map((sponsor, index) => (
                  <Box 
                    key={index}
                    sx={{
                      flex: '0 0 auto',
                      width: { xs: '100%', sm: '50%', md: '33.333%' },
                      px: 2,
                    }}
                  >
                    <Box
                      sx={{
                        p: 4,
                        transition: 'transform 0.3s ease-in-out',
                        '&:hover': { transform: 'translateY(-8px)' },
                      }}
                    >
                      <Box
                        component="img"
                        src={sponsor.logo}
                        alt={sponsor.name}
                        sx={{
                          width: '100%',
                          height: 'auto',
                          borderRadius: 2,
                          boxShadow: `0 8px 24px ${theme.palette.primary.main}20`,
                        }}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Stack>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Box 
        sx={{ 
          position: 'relative',
          isolation: 'isolate',
          py: { xs: 8, md: 12 },
          bgcolor: 'background.paper',
        }}
      >
        <Container maxWidth="lg">
          <Stack spacing={8}>
            <Stack spacing={3} alignItems="center" textAlign="center">
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  fontWeight: 700,
                  color: 'text.primary',
                }}
              >
                Benefits of Being a Sponsor
              </Typography>
            </Stack>

            <Grid container spacing={4}>
              {benefitsList.map((benefit, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      height: '100%',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: theme => `0 24px 48px ${theme.palette.purple.main}20`,
                      },
                    }}
                  >
                    <Stack spacing={2}>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 600,
                          color: theme => theme.palette.mode === 'dark' ? '#c1d82f' : 'primary.main',
                        }}
                      >
                        {benefit.title}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: 'text.secondary',
                          lineHeight: 1.6,
                        }}
                      >
                        {benefit.description}
                      </Typography>
                    </Stack>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            {/* Contact CTA */}
            <Box 
              id="contact-box"
              sx={{ 
                textAlign: 'center',
                mt: 4,
                p: 4,
                borderRadius: 2,
                bgcolor: 'background.default',
                scrollMarginTop: '100px',
              }}
            >
              <Typography variant="h6" sx={{ mb: 1, color: 'text.primary' }}>
                Interested in hearing about how collaborating with us could benefit you?
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                Feel free to reach out to us at
                {' '}
                <Box
                  component="a"
                  href="mailto:contact@austincs.org"
                  sx={{
                    color: theme => theme.palette.mode === 'dark' ? '#c1d82f' : 'primary.main',
                    textDecoration: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  contact@austincs.org
                </Box>
              </Typography>
            </Box>
          </Stack>
        </Container>
      </Box>
    </>
  );
};

export default Sponsor;
